import React from 'react';

// Cell style for consistent table appearance
const cellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: '4px 2px',
  fontSize: '14px'
};

// Stream category config
const streamCategoryConfig = {
  live_darshan:        { label: 'Live Darshan',        color: '#9C27B0' },
  live_podcasts:       { label: 'Live Podcasts',       color: '#2196F3' },
  live_discourse:      { label: 'Live Discourse',      color: '#00897B' },
  live_aarti:          { label: 'Live Aarti',          color: '#FF9800' },
  live_event_coverage: { label: 'Live Event Coverage', color: '#E53935' },
  live_pooja:          { label: 'Live Pooja',          color: '#6D4C41' },
  // legacy
  live_stream:         { label: 'Live Stream',         color: '#2196F3' },
};

// Define table columns
export const tableColumns = [
  {
    name: 'Stream Type',
    selector: row => row.stream_type,
    sortable: true,
    center: true,
    width: '130px',
    style: cellStyle,
    cell: row => {
      const config = {
        youtube:       { label: 'YouTube',  bg: '#FF0000' },
        cctv:          { label: 'Internal', bg: '#00BCD4' },
        cctv_external: { label: 'External', bg: '#FF9800' },
      }[row.stream_type] || { label: row.stream_type, bg: '#9E9E9E' };
      return (
        <span
          style={{
            minWidth: '80px',
            textAlign: 'center',
            borderRadius: '20px',
            padding: '4px 10px',
            backgroundColor: config.bg,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textTransform: 'uppercase'
          }}
        >
          {config.label}
        </span>
      );
    },
  },
  {
    name: 'Stream Category',
    selector: row => row.stream_category,
    sortable: true,
    center: true,
    width: '150px',
    style: cellStyle,
    cell: row => {
      const category = row.stream_category || 'live_darshan';
      const config = streamCategoryConfig[category] || streamCategoryConfig.live_darshan;
      return (
        <span
          style={{
            minWidth: '100px',
            textAlign: 'center',
            borderRadius: '20px',
            padding: '4px 10px',
            backgroundColor: config.color,
            color: '#FFFFFF',
            fontSize: '11px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {config.label}
        </span>
      );
    },
  },
  {
    name: 'Relations',
    center: true,
    selector: row => (row.all_relations || []).join(', '),
    sortable: false,
    width: '250px',
    style: cellStyle,
    cell: row => {
      const relations = row.all_relations || [];
      if (relations.length === 0) {
        return <span style={{ color: '#A0AEC0', fontSize: '12px' }}>—</span>;
      }
      const visible = relations.slice(0, 3);
      const overflow = relations.length - 3;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'center', padding: '4px 0' }}>
          {visible.map((name, i) => (
            <span key={i} style={{
              backgroundColor: '#EEF2FF',
              color: '#4338CA',
              fontSize: '10px',
              fontWeight: '600',
              padding: '2px 7px',
              borderRadius: '10px',
              whiteSpace: 'nowrap',
              maxWidth: '90px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>{name}</span>
          ))}
          {overflow > 0 && (
            <span style={{
              backgroundColor: '#F1F5F9',
              color: '#64748B',
              fontSize: '10px',
              fontWeight: '600',
              padding: '2px 7px',
              borderRadius: '10px',
            }}>+{overflow}</span>
          )}
        </div>
      );
    },
  },
  {
    name: 'Stream Title',
    center: true,
    selector: row => row.stream_title,
    sortable: true,
    width: '220px',
    style: cellStyle,
    cell: row => (
      <div style={{ 
        ...cellStyle,
        color: '#5662F6',
        fontWeight: '500',
        width: '100%',
        textAlign: 'center'
      }}>
        {row.stream_title}
      </div>
    ),
  },
  {
    name: 'Stream ID',
    center: true,
    selector: row => row.stream_identifier,
    sortable: true,
    width: '180px',
    style: cellStyle,
    cell: row => (
      <div style={{ 
        ...cellStyle,
        fontFamily: 'monospace',
        fontSize: '12px',
        color: '#666',
        width: '100%',
        textAlign: 'center'
      }}>
        {row.stream_identifier}
      </div>
    ),
  },
  {
    name: 'Status',
    center: true,
    selector: row => row.stream_status,
    sortable: true,
    width: '130px',
    style: cellStyle,
    cell: row => {
      const lifecycleConfig = {
        draft:         { label: 'Draft',        bg: '#9E9E9E' },
        live_now:      { label: 'Live Now',      bg: '#4CAF50' },
        processing:    { label: 'Processing',    bg: '#FF9800' },
        recently_live: { label: 'Recently Live', bg: '#2196F3' },
        archived:      { label: 'Archived',      bg: '#424242' },
      }[row.stream_status] || { label: row.stream_status, bg: '#9E9E9E' };
      return (
        <span
          style={{
            minWidth: '90px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            borderRadius: '20px',
            padding: '4px 10px',
            backgroundColor: lifecycleConfig.bg,
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {row.stream_status === 'live_now' && (
            <span style={{
              width: '7px',
              height: '7px',
              borderRadius: '50%',
              background: '#fff',
              opacity: 0.9,
              animation: 'blink 1.4s ease infinite',
              display: 'inline-block',
              marginRight: '5px',
              verticalAlign: 'middle',
              flexShrink: 0,
            }} />
          )}
          {lifecycleConfig.label}
        </span>
      );
    },
  },
  {
    name: 'Health',
    center: true,
    selector: row => row.health_status,
    sortable: true,
    width: '130px',
    id: 'health_status',
    style: cellStyle,
    cell: row => {
      if (row.stream_type === 'youtube') {
        return <span style={{ color: '#999', fontSize: '12px', textAlign: 'center' }}>N/A</span>;
      }
      if (row.stream_type === 'cctv_external') {
        return (
          <span
            style={{
              minWidth: '70px',
              textAlign: 'center',
              borderRadius: '20px',
              padding: '4px 8px',
              backgroundColor: '#78909C',
              color: '#FFFFFF',
              fontSize: '10px',
              fontWeight: '600',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            EXTERNAL
          </span>
        );
      }
      // cctv
      const healthConfig = {
        ok:      { label: 'OK',    bg: '#00C853' },
        error:   { label: 'ERROR', bg: '#F44336' },
        warning: { label: 'WARN',  bg: '#FF9800' },
      }[row.health_status] || { label: 'N/A', bg: '#9E9E9E' };
      return (
        <span
          style={{
            minWidth: '55px',
            textAlign: 'center',
            borderRadius: '20px',
            padding: '4px 8px',
            backgroundColor: healthConfig.bg,
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: '600',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          {healthConfig.label}
        </span>
      );
    },
  },
  {
    name: 'Orientation',
    selector: row => row.orientation,
    sortable: true,
    center: true,
    width: '120px',
    style: cellStyle,
    cell: row => (
      <span
        style={{
          minWidth: '80px',
          textAlign: 'center',
          borderRadius: '20px',
          padding: '4px 10px',
          backgroundColor: row.orientation === 'landscape' ? '#3F51B5' : '#E91E63',
          color: '#FFFFFF',
          fontSize: '11px',
          fontWeight: '600',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        {row.orientation === 'landscape' ? 'Landscape' : 'Portrait'}
      </span>
    ),
  },
  {
    name: 'Featured',
    center: true,
    selector: row => row.is_featured,
    sortable: true,
    width: '100px',
    style: cellStyle,
    cell: row => (
      <span style={{ fontSize: '14px' }}>
        {row.is_featured ? 'Yes' : 'No'}
      </span>
    ),
  },
  {
    name: 'Created Date',
    center: true,
    selector: row => row.created_at,
    sortable: true,
    width: '120px',
    style: cellStyle,
  },
  {
    name: 'Action',
    center: true,
    selector: row => row.action,
    sortable: false,
    width: '170px',
    id: 'action',
    style: cellStyle,
  }
];